import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { listProducts, PRODUCT_SORT } from '../../api/products';
import { Breadcrumbs } from '../../components/ui/Breadcrumbs';
import { ProductCard } from '../../components/ui/ProductCard';
import { CatalogFilters, buildPriceFilter } from '../../components/catalog/CatalogFilters';
import { CategoryFilterTag } from '../../components/catalog/CategoryFilterTag';
import { DEFAULT_PAGE_SIZE, PageSizeSelect, parsePageSize } from '../../components/catalog/PageSizeSelect';
import { SortSelect } from '../../components/catalog/SortSelect';
import { Seo } from '../../components/seo';
import { useCategories } from '../../context/CategoriesContext';
import { catalogSeo } from '../../config/seo';
import { SITE } from '../../config/site';
import { buildBreadcrumbJsonLd } from '../../seo/buildBreadcrumbJsonLd';
import { resolveCategoryFilterIds } from '../../utils/buildCategoryTree';
import { parseTagIds, serializeTagIds } from '../../utils/parseTagIds';
import styles from './CatalogPage.module.css';

const SORT_MAP = {
  relevance: PRODUCT_SORT.RELEVANCE,
  new: PRODUCT_SORT.NEW,
  name_asc: PRODUCT_SORT.NAME_ASC,
  name_desc: PRODUCT_SORT.NAME_DESC,
  price_asc: PRODUCT_SORT.PRICE_ASC,
  price_desc: PRODUCT_SORT.PRICE_DESC,
};

function readTagsParam(searchParams) {
  return searchParams.get('tags') || searchParams.get('tag_ids') || '';
}

function useCatalogParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const category = searchParams.get('category') || '';
  const query = searchParams.get('q') || '';
  const sort = searchParams.get('sort') || 'relevance';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parsePageSize(searchParams.get('pageSize'));
  const minPrice = searchParams.get('minPrice') || '';
  const maxPrice = searchParams.get('maxPrice') || '';
  const tagsParam = readTagsParam(searchParams);
  const tagIds = useMemo(() => parseTagIds(tagsParam), [tagsParam]);

  const updateParams = useCallback(
    (updates) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === '' || value === null || value === undefined) {
            next.delete(key);
          } else if (Array.isArray(value)) {
            const serialized = serializeTagIds(value);
            if (!serialized) {
              next.delete(key);
            } else {
              next.set(key, serialized);
            }
          } else {
            next.set(key, String(value));
          }
        });

        // Prefer canonical `tags` query key
        if (Object.prototype.hasOwnProperty.call(updates, 'tags')) {
          next.delete('tag_ids');
        }

        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  return { category, query, sort, page, pageSize, minPrice, maxPrice, tagIds, updateParams };
}

export function CatalogPage({ title = 'Каталог' }) {
  const { category, query, sort, page, pageSize, minPrice, maxPrice, tagIds, updateParams } =
    useCatalogParams();
  const { categories, tree } = useCategories();

  const [draftMin, setDraftMin] = useState(minPrice);
  const [draftMax, setDraftMax] = useState(maxPrice);
  const [draftCategory, setDraftCategory] = useState(category);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setDraftMin(minPrice);
    setDraftMax(maxPrice);
    setDraftCategory(category);
  }, [minPrice, maxPrice, category]);

  const categoryName = useMemo(
    () => categories.find((item) => item.id === category)?.name || '',
    [categories, category],
  );

  const categoryFilterIds = useMemo(
    () => resolveCategoryFilterIds(category, tree),
    [category, tree],
  );

  const apiFilter = useMemo(() => {
    const filter = { ...buildPriceFilter(minPrice, maxPrice) };
    if (query) filter.query = query;
    if (categoryFilterIds.length > 0) {
      filter.category_ids = categoryFilterIds;
    }
    if (tagIds.length > 0) {
      filter.tag_ids = tagIds;
    }
    return filter;
  }, [minPrice, maxPrice, query, categoryFilterIds, tagIds]);

  useEffect(() => {
    setLoading(true);
    setError(null);

    listProducts({
      filter: apiFilter,
      sort: SORT_MAP[sort] || PRODUCT_SORT.RELEVANCE,
      pagination: { page, page_size: String(pageSize) },
    })
      .then((data) => {
        setProducts(data.products || []);
        setTotal(data.pagination?.total || 0);
      })
      .catch((err) => {
        setError(err.message);
        setProducts([]);
      })
      .finally(() => setLoading(false));
  }, [apiFilter, sort, page, pageSize]);

  const totalPages = Math.ceil(total / pageSize);

  const pageTitle = categoryName || (query ? `Поиск: ${query}` : title);
  const seo = catalogSeo({ categoryName, query });
  const breadcrumbs = [
    { label: 'Главная', to: '/' },
    { label: 'Каталог', to: '/catalog' },
    ...(categoryName || query ? [{ label: categoryName || `«${query}»` }] : []),
  ];

  const handleFilterChange = ({ minPrice: min, maxPrice: max, category: nextCategory }) => {
    if (min !== undefined) setDraftMin(min);
    if (max !== undefined) setDraftMax(max);
    if (nextCategory !== undefined) setDraftCategory(nextCategory);
  };

  const handleApply = () => {
    updateParams({
      minPrice: draftMin,
      maxPrice: draftMax,
      category: draftCategory,
      page: 1,
    });
  };

  const handleReset = () => {
    setDraftMin('');
    setDraftMax('');
    setDraftCategory('');
    updateParams({ minPrice: '', maxPrice: '', category: '', tags: '', q: '', page: 1 });
  };

  const handleRemoveCategory = () => {
    updateParams({ category: '', page: 1 });
  };

  const handleToggleTag = (tagId) => {
    const id = String(tagId);
    const next = tagIds.includes(id)
      ? tagIds.filter((item) => item !== id)
      : [...tagIds, id];
    updateParams({ tags: next, page: 1 });
  };

  return (
    <div className={`container ${styles.page}`}>
      <Seo
        title={seo.title}
        description={seo.description}
        path="/catalog"
        jsonLd={buildBreadcrumbJsonLd(breadcrumbs)}
      />

      <Breadcrumbs
        items={[
          { label: 'Главная', to: '/' },
          { label: pageTitle },
        ]}
      />
      <h1 className={styles.title}>
        {categoryName
          ? `${categoryName} в ${SITE.city}е`
          : query
            ? `Поиск: ${query}`
            : `${title} товаров для кондитеров`}
      </h1>

      <div className={styles.layout}>
        <CatalogFilters
          category={draftCategory}
          tagIds={tagIds}
          minPrice={draftMin}
          maxPrice={draftMax}
          onChange={handleFilterChange}
          onToggleTag={handleToggleTag}
          onApply={handleApply}
          onReset={handleReset}
        />

        <div className={styles.content}>
          {category && (
            <CategoryFilterTag label={categoryName} onRemove={handleRemoveCategory} />
          )}

          <div className={styles.toolbar}>
            <div className={styles.toolbarControls}>
              <SortSelect
                value={sort}
                onChange={(val) => updateParams({ sort: val, page: 1 })}
              />
              <PageSizeSelect
                value={pageSize}
                onChange={(val) =>
                  updateParams({
                    pageSize: val === DEFAULT_PAGE_SIZE ? '' : val,
                    page: 1,
                  })
                }
              />
            </div>
            {!loading && (
              <span className={styles.count}>{total} товаров</span>
            )}
          </div>

          {error && <p className={styles.error}>{error}</p>}

          {loading ? (
            <div className={styles.grid}>
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className={styles.skeleton} />
              ))}
            </div>
          ) : products.length === 0 ? (
            <p className={styles.empty}>Товары не найдены</p>
          ) : (
            <div className={styles.grid}>
              {products.map((product) => (
                <ProductCard key={product.id} product={product} showOpenButton />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => updateParams({ page: page - 1 })}
              >
                ← Назад
              </button>
              <span>
                {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => updateParams({ page: page + 1 })}
              >
                Вперёд →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
